import type React from "react";
import { useState } from "react";
import { AppDesignTable, CustomizeSpecTable, FieldDesignTable } from "~/components/Document/tables";
import { useMarkdownRenderer } from "~/components/Document/tables/hooks/useMarkdownRenderer";
import styles from "./index.module.scss";

type PreviewMode = "css" | "iframe";

interface DocumentPreviewProps {
  content?: string | undefined;
  documentType: "app-design" | "field-design" | "customize-specification";
  documentUrl?: string | undefined;
  previewMode?: PreviewMode | undefined;
  height?: number | undefined;
}

// コンテンツを正規化（JSON文字列形式の場合はパースして取り出す）
const normalizeContent = (
  content: string,
): { type: "markdown" | "json"; content: string; parsed?: unknown } => {
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed === "object" && parsed !== null) {
      return { type: "json", content, parsed };
    }
    if (typeof parsed === "string") {
      return { type: "markdown", content: parsed };
    }
  } catch {
    // JSONパースに失敗した場合はMarkdownとして扱う
  }
  return { type: "markdown", content };
};

// CSSプレビューモード - 共通テーブルを使用
const CssPreview: React.FC<{
  content?: string | undefined;
  documentType: DocumentPreviewProps["documentType"];
}> = ({ content, documentType }) => {
  const { renderMarkdown } = useMarkdownRenderer();

  if (!content) {
    return <div className={styles["emptyPreview"]} />;
  }

  // コンテンツを正規化
  const normalized = normalizeContent(content);

  // JSON形式の場合は共通テーブルコンポーネントを使用
  if (normalized.type === "json" && normalized.parsed) {
    switch (documentType) {
      case "app-design":
        return (
          <div className={styles["previewContainer"]}>
            <AppDesignTable content={content} mode="compact" maxRows={3} />
          </div>
        );
      case "field-design":
        return (
          <div className={styles["previewContainer"]}>
            <FieldDesignTable content={content} mode="compact" maxApps={3} maxFields={5} />
          </div>
        );
      case "customize-specification":
        return (
          <div className={styles["previewContainer"]}>
            <CustomizeSpecTable content={content} mode="compact" maxRows={4} />
          </div>
        );
      default:
        break;
    }
  }

  // Markdownの場合 - Markdownとしてレンダリング
  return (
    <div className={styles["previewContainer"]}>
      <div className={styles["markdownPreview"]}>{renderMarkdown(normalized.content)}</div>
    </div>
  );
};

// iframeプレビューモード
const IframePreview: React.FC<{
  documentUrl?: string | undefined;
  height?: number | undefined;
}> = ({ documentUrl, height = 200 }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  if (!documentUrl) {
    return <div className={styles["emptyPreview"]}>プレビューを表示できません</div>;
  }

  const scale = height / 800;

  return (
    <div className={styles["iframeContainer"]} style={{ height }}>
      {!isLoaded && <div className={styles["iframeLoading"]}>読み込み中...</div>}
      <iframe
        src={documentUrl}
        className={styles["previewIframe"]}
        style={{
          transform: `scale(${scale})`,
          width: `${100 / scale}%`,
          height: `${100 / scale}%`,
        }}
        onLoad={() => setIsLoaded(true)}
        title="Document Preview"
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
};

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  content,
  documentType,
  documentUrl,
  previewMode = "css",
  height = 200,
}) => {
  return (
    <div className={styles["documentPreview"]} style={{ height }}>
      {previewMode === "css" ? (
        <CssPreview content={content} documentType={documentType} />
      ) : (
        <IframePreview documentUrl={documentUrl} height={height} />
      )}
    </div>
  );
};

export default DocumentPreview;
